import { useContext, useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Checkbox,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import EditIcon from '@mui/icons-material/Edit';
import { useQueryClient } from 'react-query';
import { PROFORMAS_KEY } from '../../api/hooks/cache-keys';
import { useGetColors } from '../../api/hooks/inventory';
import {
  useCreateProforma,
  useGetFirms,
  useGetFirmsBankAccounts,
  useGetModels,
} from '../../api/hooks/sales';
import { accountToString, toPersianNumber } from '../../utils/helpers';
import { ToastContext } from '../../contexts/toast';
import client from '../../api/hooks/axios-client';
import { AVAILABLE_MODELS_URL, AVAILABLE_URL } from '../../api/requests/urls';
import { ModalContext } from '../../contexts/modal';
import NumberFormatCustom from '../common/price-value-input';
import { numberToWords } from '@persian-tools/persian-tools';

export default function NewPerformaForm() {
  const queryClient = useQueryClient();
  const { setOpen } = useContext(ModalContext);

  const { data: buyers } = useGetFirms(false);
  const { data: sellers } = useGetFirms(true);

  const { data: models } = useGetModels();
  const { data: colors } = useGetColors();
  const [seller, setSeller] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [prices, setPrices] = useState({});
  const [availableModels, setAvailableModels] = useState([]);

  const [modelId, setModelId] = useState(null);
  const [colorId, setColorId] = useState(null);
  const [details, setDetails] = useState(null);
  const [bankAccountIds, setBankAccountIds] = useState([]);
  const [amount, setAmount] = useState(0);
  const [unitPrice, setUnitPrice] = useState(null);
  const [available, setAvailable] = useState(0);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState([]);
  const processedRows = processRows(rows);

  const { showToast } = useContext(ToastContext);

  const aggregatedData = aggregateData(processedRows);
  const discount = aggregatedData?.totalPrice % 1000;
  const createProforma = useCreateProforma();
  const { data: firmsAccounts } = useGetFirmsBankAccounts(seller, { enabled: seller !== null });

  async function handleCreateProforma() {
    await createProforma.mutateAsync(
      {
        seller,
        buyer,
        discount,
        bankAccountIds,
        details,
        // eslint-disable-next-line no-unused-vars
        rows: rows.map(({ id, ...others }) => others),
      },
      {
        onSuccess: () => {
          showToast('پیش‌فاکتور جدید ساخته شد.', 'success');
        },
      }
    );
    await queryClient.invalidateQueries(PROFORMAS_KEY());
    setOpen(false);
  }

  function addRow() {
    if (
      modelId !== null &&
      colorId !== null &&
      Number(amount) > 0 &&
      Number(amount) <= available &&
      Number(unitPrice) !== 0
    ) {
      setRows((rows) => [
        ...rows,
        { modelId, colorId, amount, unitPrice: adjustPrice(unitPrice, amount), id: rows.length },
      ]);
      setPrices((p) => ({ ...p, [modelId]: Math.floor(unitPrice) }));
      setModelId(null);
      setColorId(null);
      setAmount(0);
      setAvailable(0);
      setUnitPrice(0);
      setDetails(null);
      setError(false);
    } else {
      if (!modelId) {
        setError('model');
      } else if (!colorId) {
        setError('color');
      } else if (!unitPrice) {
        setError('price');
      } else if (!seller) {
        setError('seller');
      } else if (Number(amount) > available || Number(amount) <= 0) {
        setError('amount');
      }
    }
  }

  function editRow(index) {
    const { modelId, colorId, amount, unitPrice } = rows.find((r) => r.id == index);
    console.log(rows.filter((r) => r.id !== index));
    rows.splice(index, 1);
    setModelId(modelId);
    setColorId(colorId);
    setAmount(amount);
    setUnitPrice(Math.floor(unitPrice * 1.10));
  }

  const columns = [
    {
      id: 'id',
      label: 'حذف',
      minWidth: 35,
      format: (id) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={() => setRows((rows) => rows.filter((r) => r.id !== id))}
          >
            <HighlightOffIcon />
          </IconButton>
          <IconButton size="small" onClick={() => editRow(id)}>
            <EditIcon />
          </IconButton>
        </Stack>
      ),
    },
    {
      id: 'modelId',
      label: 'مدل',
      minWidth: 100,
      format: (id) => {
        const model = models.find((m) => m.id === id);
        return `${model.name} (${model.ram}/${model.memory})`;
      },
    },
    {
      id: 'colorId',
      label: 'رنگ',
      minWidth: 70,
      format: (id) => colors.find((c) => c.id === id).name,
    },
    {
      id: 'unitPrice',
      label: 'قیمت واحد (ریال)',
      minWidth: 70,
      format: (p) => toPersianNumber(p, true),
    },
    {
      id: 'taxedUnitPrice',
      label: 'قیمت واحد + مالیات (ریال)',
      minWidth: 70,
      styles: { fontWeight: 'bold', fontSize: 'large' },
      format: (p) => toPersianNumber(p, true),
    },
    { id: 'amount', label: 'تعداد', minWidth: 70, format: toPersianNumber },
    {
      id: 'totalPrice',
      label: 'قیمت کل (ریال)',
      minWidth: 70,
      format: (p) => toPersianNumber(p, true),
    },
  ];

  useEffect(() => {
    if (modelId !== null && colorId !== null && seller !== null)
      client
        .get(AVAILABLE_URL + `?model=${modelId}&color=${colorId}&owner=${seller}`)
        .then((r) => setAvailable(r));
    if (prices[modelId] && !unitPrice) {
      setUnitPrice(prices[modelId]);
    }
  }, [modelId, colorId, seller]);

  useEffect(() => {
    if (seller) {
      client.get(AVAILABLE_MODELS_URL + `/${seller}`).then((r) => setAvailableModels(r));
    }
  }, [seller]);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    setBankAccountIds(typeof value === 'string' ? value.split(',') : value);
  };

  return (
    <Container sx={{ width: { xs: '300px', md: '1200px' } }}>
      <Grid container sx={{ py: 2 }} spacing={2}>
        <Grid item xs={6} md={4}>
          <TextField
            size="small"
            label="فروشنده"
            fullWidth
            select
            inputProps={{ readOnly: rows.length > 0 }}
            onChange={(e) => setSeller(e.target.value)}
            error={error === 'seller'}
          >
            {sellers?.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={4}>
          <Autocomplete
            size="small"
            fullWidth
            onChange={(event, newValue) => {
              setBuyer(newValue?.id);
            }}
            options={buyers?.filter((buyer) => buyer.id !== seller)}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => <TextField {...params} label="خریدار" />}
          ></Autocomplete>
        </Grid>
        <Grid item xs={12} md={4}>
          {/* <TextField
            size="small"
            label="شماره حساب"
            fullWidth
            select
            value={bankAccountId}
            SelectProps={{
              multiple: true,
              value: bankAccountId,
              onChange: (e) => setBankAccountId(e.target.value),
            }}
          >
            {firmsAccounts?.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {`${item.bank} (${toPersianNumberString(item.accountNumber.slice(-5))})`}
              </MenuItem>
            ))}
          </TextField> */}
          <FormControl fullWidth size="small">
            <InputLabel id="demo-multiple-checkbox-label">شماره حساب</InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              multiple
              value={bankAccountIds}
              onChange={handleChange}
              input={<OutlinedInput label="شماره حساب" />}
              renderValue={(selected) =>
                selected
                  .map((id) => accountToString(firmsAccounts?.find((account) => account.id === id)))
                  .join(' یا ')
              }
            >
              {firmsAccounts?.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  <Checkbox checked={bankAccountIds.indexOf(account.id) > -1} />
                  <ListItemText primary={accountToString(account)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={7} md={3}>
          <TextField
            size="small"
            label="مدل"
            fullWidth
            select
            value={modelId}
            error={error == 'model'}
            onChange={(e) => setModelId(e.target.value)}
          >
            {models
              .filter((m) => availableModels.some((r) => r.modelId == m.id))
              ?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {`${item.name} (${item.ram}/${item.memory})`}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid item xs={5} md={2}>
          <TextField
            size="small"
            label="رنگ"
            fullWidth
            select
            value={colorId}
            error={error === 'color'}
            onChange={(e) => setColorId(e.target.value)}
          >
            {colors
              .filter((c) => availableModels.some((r) => r.colorId == c.id && r.modelId == modelId))
              ?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid item xs={7} md={3}>
          <TextField
            size="small"
            label="قیمت تمام‌شده (ریال)"
            fullWidth
            onChange={(e) => setUnitPrice(e.target.value)}
            InputProps={{
              inputComponent: NumberFormatCustom,
              value: unitPrice,
            }}
            FormHelperTextProps={{ sx: { fontSize: 10, wordSpacing: '0px' } }}
            helperText={numberToWords(Math.floor(unitPrice / 10)) + ' تومان'}
            error={error === 'price'}
          />
        </Grid>
        <Grid item xs={5} md={2}>
          <TextField
            size="small"
            label="تعداد"
            fullWidth
            inputProps={{ max: available }}
            type="number"
            value={amount}
            helperText={`موجودی: ${available?.toLocaleString('fa-IR')}`}
            onChange={(e) => setAmount(e.target.value)}
            error={error === 'amount'}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <Button onClick={addRow} variant="contained" fullWidth>
            افزدون ردیف
          </Button>
        </Grid>
        <Grid item xs={12} sx={{ my: 2 }}>
          <TableContainer sx={{ maxHeight: { xs: 150, md: 280 } }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth, fontWeight: '600' }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {processedRows?.map((row) => {
                  return (
                    <TableRow hover tabIndex={-1} key={`${row.name}-${row.id}`}>
                      {columns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell key={column.id} align={column.align} style={column.styles}>
                            {column.format ? column.format(value) : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                <TableRow hover>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell>(با حساب ٪۹ مالیات)</TableCell>
                  <TableCell>{toPersianNumber(aggregatedData.totalAmount)}</TableCell>
                  <TableCell>{toPersianNumber(aggregatedData.totalPrice, true)}</TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell>تخفیف</TableCell>
                  <TableCell>{toPersianNumber(discount, true)}</TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell>مبلغ نهایی</TableCell>
                  <TableCell>
                    {toPersianNumber(aggregatedData.totalPrice - discount, true)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={5} md={3}>
          <TextField
            size="small"
            label="تخفیف (ریال)"
            fullWidth
            value={discount}
            InputProps={{
              inputComponent: NumberFormatCustom,
              readOnly: true,
            }}
          />
        </Grid>
        <Grid item xs={7} md={6}>
          <TextField
            size="small"
            label="توضیحات"
            fullWidth
            onChange={(e) => setDetails(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Button onClick={handleCreateProforma} fullWidth variant="contained">
            ثبت
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
}

function processRows(rows) {
  return rows?.map((r) => ({
    ...r,
    taxedUnitPrice: Math.ceil(1.10 * Number(r.unitPrice)),
    totalPrice: Number(r.amount) * Number(r.unitPrice),
  }));
}

function aggregateData(rows) {
  const totalPrice = Math.floor(rows?.reduce((p, c) => p + c.totalPrice * 1.10, 0));
  const totalAmount = rows?.reduce((p, c) => p + Number(c.amount), 0);
  return { totalPrice, totalAmount };
}

function adjustPrice(price) {
  return Math.ceil((100 * price) / 110);
}
